// ------------------------------------------------------------
// stories in motion — reel data
// ------------------------------------------------------------
// To add a new video/portfolio piece, just add another object
// to this array. videoId is the part after "v=" (or after
// youtu.be/) in the YouTube URL. That's it — the reel below
// handles rendering, thumbnails, lazy-loading the player, drag
// scroll, snapping, and the progress bar automatically.
export const filmReel = [
  {
    videoId: "tD3sF5fa54M",
    title: "Theseus Robotics 2025 Robot Reveal",
    subtitle: "Theseus Robotics · 2025",
    desc: "Theseus Robotics' final reveal for our 2024-2025 competition robot.",
  },
  {
     videoId: "tw757O7Mw2o",
    title: "The Last Train Home",
    subtitle: "UNSW Railway Society · 2026",
    desc: "Sydney's transport network is far from perfect. But it'll always find a way to get you home. Nominated for the 2026 UNSW Outstanding Multimedia Award.",
  },
  {
     videoId: "Th5B8Agd4Ag",
    title: "You can't drive here.",
    subtitle: "Personal Project · 2026",
    desc: "I travel to and document the only train station in Australia that is inaccessible by car.",
  }
];

// ------------------------------------------------------------
// stories in motion — horizontal reel
// ------------------------------------------------------------
function ytThumb(id) {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

// optional external resources per item: links: [{ label, url }]
function buildLinks(links) {
  if (!links || !links.length) return "";
  const items = links
    .map(
      (l) =>
        `<a class="reel__link mono" href="${l.url}" target="_blank" rel="noopener noreferrer">${l.label} ↗</a>`
    )
    .join("");
  return `<div class="reel__links">${items}</div>`;
}

function buildCard(item, index) {
  const el = document.createElement("article");
  el.className = "reel__item";
  el.setAttribute("data-fade", "");
  el.innerHTML = `
    <button class="reel__thumb" type="button" data-cursor="view" aria-label="Play ${item.title}">
      <img class="reel__thumb-img" src="${ytThumb(item.videoId)}" alt="" loading="lazy" decoding="async" />
      <span class="reel__play">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
    <div class="reel__meta">
      <span class="reel__no mono">r.${String(index + 1).padStart(2, "0")}</span>
      <h3 class="reel__title">${item.title}</h3>
      <span class="reel__subtitle mono">${item.subtitle}</span>
      <p class="reel__desc">${item.desc}</p>
      ${buildLinks(item.links)}
    </div>
  `;

  const thumb = el.querySelector(".reel__thumb");
  thumb.addEventListener("click", () => {
    if (el.dataset.playing) return;
    el.dataset.playing = "1";
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.youtube.com/embed/${item.videoId}?autoplay=1&rel=0`;
    iframe.title = item.title;
    iframe.frameBorder = "0";
    iframe.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
    iframe.allowFullscreen = true;
    thumb.replaceWith(iframe);
    iframe.className = "reel__thumb reel__thumb--iframe";
  });

  return el;
}

// root defaults to the first reel on the page; data defaults to the main reel
export function initFilmReel(root = document.querySelector("[data-reel]"), data = filmReel) {
  if (!root) return;

  const track = root.querySelector("[data-reel-track]");
  const prevBtn = root.querySelector("[data-reel-prev]");
  const nextBtn = root.querySelector("[data-reel-next]");
  const progressBar = root.querySelector("[data-reel-progress-bar]");

  data.forEach((item, i) => track.appendChild(buildCard(item, i)));

  // single-item reels don't need scroll chrome — hide it until there's more to browse
  if (data.length <= 1) {
    root.classList.add("reel--single");
    return;
  }

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function cardStep() {
    const card = track.querySelector(".reel__item");
    if (!card) return 0;
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || 0);
    return card.getBoundingClientRect().width + gap;
  }

  function updateProgress() {
    const max = track.scrollWidth - track.clientWidth;
    const pct = max > 0 ? track.scrollLeft / max : 0;
    if (progressBar) progressBar.style.transform = `scaleX(${Math.min(1, Math.max(0, pct))})`;
    if (prevBtn) prevBtn.disabled = track.scrollLeft <= 4;
    if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 4;
  }

  track.addEventListener("scroll", updateProgress, { passive: true });
  window.addEventListener("resize", updateProgress);
  updateProgress();

  prevBtn?.addEventListener("click", () => {
    track.scrollBy({ left: -cardStep(), behavior: reduced ? "auto" : "smooth" });
  });
  nextBtn?.addEventListener("click", () => {
    track.scrollBy({ left: cardStep(), behavior: reduced ? "auto" : "smooth" });
  });

  // click-and-drag scroll — tracked on window (not pointer capture) so a
  // plain click on a thumbnail still reaches its own click handler
  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;

  track.addEventListener("pointerdown", (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    dragging = true;
    moved = false;
    startX = e.clientX;
    startScroll = track.scrollLeft;
  });

  window.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 4 && !moved) {
      moved = true;
      track.classList.add("is-dragging");
    }
    if (moved) track.scrollLeft = startScroll - dx;
  });

  function endDrag() {
    if (!dragging) return;
    dragging = false;
    track.classList.remove("is-dragging");
    if (moved) {
      // swallow the click that follows a real drag so it doesn't trigger play
      const onClick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener("click", onClick, { capture: true, once: true });
    }
  }
  window.addEventListener("pointerup", endDrag);
  window.addEventListener("pointercancel", endDrag);
}
